import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps } from "../../src/shared/google_maps_loader"
import MapStyle from "src/shared/resources/map-style-light.json"
import MarkerIcon from "images/map/map-marker.png"

// Not clickable: the circle covers the sectors and would swallow their hover events.
const CIRCLE_OPTIONS = {
  strokeWeight: 0,
  fillColor: "#3BB3DE",
  fillOpacity: 0.1,
  clickable: false,
  zIndex: 1
}

const SECTOR_OPTIONS = { fillOpacity: 0.3, zIndex: 2 }
const SECTOR_HOVER_OPTIONS = { fillOpacity: 0.55 }

export default class extends Controller {
  static targets = ["canvas", "mapSection", "radius", "latitude", "longitude", "city"]
  static values = {
    lat: Number,
    lng: Number,
    radius: Number,
    sectors: { type: Array, default: [] }
  }

  connect () {
    this.sectorPolygons = []
    if (this.hasCoordinates()) this.initMap()
  }

  disconnect () {
    this.clearOverlays()
    this.sectorInfoWindow?.close()
    this.sectorInfoWindow = null
    this.marker = null
    this.circle = null
    this.map = null
    this.maps = null
  }

  addressChanged (event) {
    const latitude = Number(event.detail.latitude)
    const longitude = Number(event.detail.longitude)

    if (!latitude || !longitude) {
      this.clearLocation()
      return
    }

    this.latValue = latitude
    this.lngValue = longitude
    this.showMap()
    this.syncMap()
  }

  updateRadius () {
    this.radiusValue = Number(this.radiusTarget.value)
    this.syncCircle()
    this.syncZoom()
  }

  async initMap () {
    if (this.map || !this.hasCoordinates()) return

    try {
      this.maps = await loadGoogleMaps()
      const center = this.center()

      this.map = new this.maps.Map(this.canvasTarget, {
        center,
        zoom: this.zoomFromRadius(),
        styles: MapStyle,
        maxZoom: 19,
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          position: this.maps.ControlPosition.LEFT_TOP
        }
      })

      this.marker = new this.maps.Marker({
        position: center,
        map: this.map,
        icon: MarkerIcon,
        draggable: true,
        optimized: false
      })
      this.marker.addListener("dragend", () => {
        const position = this.marker.getPosition()
        this.setCoordinates(position.lat(), position.lng())
        this.syncCircle()
      })

      this.syncCircle()
      this.renderSectors()
    } catch (_error) {
      // Keep the empty map container if the script fails to load.
    }
  }

  async syncMap () {
    if (!this.hasCoordinates()) return

    if (!this.map) {
      await this.initMap()
      return
    }

    const center = this.center()
    this.map.setCenter(center)
    this.marker.setPosition(center)
    this.syncCircle()
    this.syncZoom()
  }

  syncCircle () {
    if (!this.map || !this.maps || !this.hasCoordinates()) return

    const radius = this.radiusInMeters()
    if (!radius) {
      this.circle?.setMap(null)
      this.circle = null
      return
    }

    if (!this.circle) {
      this.circle = new this.maps.Circle({
        map: this.map,
        center: this.center(),
        radius,
        ...CIRCLE_OPTIONS
      })
      return
    }

    this.circle.setCenter(this.center())
    this.circle.setRadius(radius)
  }

  syncZoom () {
    if (!this.map) return

    this.map.setZoom(this.zoomFromRadius())
  }

  renderSectors () {
    if (!this.map || !this.maps) return

    this.clearSectorPolygons()
    this.sectorsValue.forEach((sector) => {
      if (!Array.isArray(sector.coordinates) || !sector.coordinates.length) return

      const polygon = new this.maps.Polygon({
        map: this.map,
        paths: sector.coordinates,
        ...SECTOR_OPTIONS
      })
      this.bindSectorTooltip(polygon, sector)
      this.sectorPolygons.push(polygon)
    })
  }

  // Hovering a sector shows its name and active practitioners count next to the cursor.
  bindSectorTooltip (polygon, sector) {
    if (!sector.name) return

    polygon.addListener("mouseover", (event) => {
      polygon.setOptions(SECTOR_HOVER_OPTIONS)
      this.openSectorInfoWindow(sector, event.latLng)
    })
    polygon.addListener("mousemove", (event) => this.sectorInfoWindow?.setPosition(event.latLng))
    polygon.addListener("mouseout", () => {
      polygon.setOptions(SECTOR_OPTIONS)
      this.sectorInfoWindow?.close()
    })
  }

  openSectorInfoWindow (sector, position) {
    this.sectorInfoWindow ||= new this.maps.InfoWindow({ disableAutoPan: true, headerDisabled: true })
    this.sectorInfoWindow.setContent(this.sectorTooltipContent(sector))
    this.sectorInfoWindow.setPosition(position)
    this.sectorInfoWindow.open({ map: this.map, shouldFocus: false })
  }

  sectorTooltipContent (sector) {
    const content = document.createElement("div")
    const name = document.createElement("strong")
    name.textContent = sector.name
    content.append(name)

    if (sector.practitionersLabel) {
      const count = document.createElement("div")
      count.textContent = sector.practitionersLabel
      content.append(count)
    }

    return content
  }

  clearLocation () {
    this.latValue = NaN
    this.lngValue = NaN
    if (this.hasRadiusTarget) this.radiusTarget.value = ""
    this.radiusValue = NaN
    this.hideMap()
    this.clearOverlays()
    this.map = null
  }

  setCoordinates (latitude, longitude) {
    this.latValue = latitude
    this.lngValue = longitude
    if (this.hasLatitudeTarget) this.latitudeTarget.value = latitude
    if (this.hasLongitudeTarget) this.longitudeTarget.value = longitude
  }

  showMap () {
    if (this.hasMapSectionTarget) this.mapSectionTarget.hidden = false
  }

  hideMap () {
    if (this.hasMapSectionTarget) this.mapSectionTarget.hidden = true
  }

  hasCoordinates () {
    return !Number.isNaN(this.latValue) && !Number.isNaN(this.lngValue) && this.latValue && this.lngValue
  }

  center () {
    return { lat: this.latValue, lng: this.lngValue }
  }

  radiusInMeters () {
    const radius = Number(this.hasRadiusTarget ? this.radiusTarget.value : this.radiusValue)
    if (!radius || Number.isNaN(radius)) return 0

    return radius * 1000
  }

  zoomFromRadius () {
    const radius = Number(this.hasRadiusTarget ? this.radiusTarget.value : this.radiusValue)
    if (!radius || Number.isNaN(radius)) return 12

    return Math.round(14 - Math.log(radius) / Math.LN2)
  }

  clearOverlays () {
    this.marker?.setMap(null)
    this.circle?.setMap(null)
    this.clearSectorPolygons()
  }

  clearSectorPolygons () {
    this.sectorPolygons.forEach((polygon) => polygon.setMap(null))
    this.sectorPolygons = []
  }
}
